import { journeyStorage } from '@epilot/journey-logic-commons'

/** Survives the SSO full-page redirects; read via
 * `JSON.parse(localStorage.getItem('jsso-debug') ?? '[]')` in the console. */
export const SSO_TRACE_KEY = 'jsso-debug'

const MAX_ENTRIES = 10
const MAX_DETAIL_LENGTH = 200

export type SsoTracePhase =
  | 'start'
  | 'embed-start'
  | 'redirect'
  | 'callback'
  | 'handoff'
  | 'exchange-browser'
  | 'exchange-backend'
  | 'nonce'
  | 'login'
  | 'challenge'
  | 'pending-activation'
  | 'settle'
  | 'restore'
  | 'success'

export type SsoTraceEntry = {
  ts: number
  phase: SsoTracePhase
  ok: boolean
  errorName?: string
  /** Journey translation key the SSO notice rendered for this failure. */
  errorKey?: string
  /** HTTP status of the failed exchange/login call, when one was made. */
  status?: number
  detail?: string
}

/** Defense in depth: even caller-provided detail must never persist query
 * strings (code/state), JWT-shaped tokens, or email addresses. */
const scrubDetail = (detail: string): string =>
  detail
    .replace(/\?\S+/g, '?<redacted>')
    .replace(/eyJ[\w-]{4,}\.[\w-]+\.?[\w-]*/g, '<jwt>')
    .replace(/\S+@\S+\.\S+/g, '<email>')
    .slice(0, MAX_DETAIL_LENGTH)

/** Reads the persisted trace. Exported for tests only. */
export const readSsoTrace = (): SsoTraceEntry[] => {
  try {
    const parsed = JSON.parse(journeyStorage.getItem(SSO_TRACE_KEY) ?? '[]')

    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/** Appends a PII-safe entry to the localStorage ring buffer (last 10 kept).
 * Purely additive observability: it must never throw into the SSO flow. */
export const recordSsoTrace = (entry: Omit<SsoTraceEntry, 'ts'>): void => {
  try {
    const entries = readSsoTrace()

    entries.push({
      ts: Date.now(),
      ...entry,
      ...(entry.detail !== undefined && { detail: scrubDetail(entry.detail) })
    })

    journeyStorage.setItem(
      SSO_TRACE_KEY,
      JSON.stringify(entries.slice(-MAX_ENTRIES))
    )
  } catch {
    // tracing is best-effort; storage/JSON failures must not surface
  }
}

const tracedErrors = new WeakSet<object>()

/** Records once per error object, so an outer catch-all does not duplicate
 * the more precise entry a nested failure site already wrote. */
export const recordSsoTraceOnce = (
  error: unknown,
  entry: Omit<SsoTraceEntry, 'ts'>
): void => {
  if (typeof error === 'object' && error !== null) {
    if (tracedErrors.has(error)) return

    tracedErrors.add(error)
  }

  recordSsoTrace(entry)
}

type MaybeHttpError = {
  response?: { status?: number; data?: { error?: string; reason?: string } }
}

/** Best-effort HTTP status off an axios-shaped API error. */
export const httpStatusFromError = (error: unknown): number | undefined => {
  const status = (error as MaybeHttpError)?.response?.status

  return typeof status === 'number' ? status : undefined
}

/** OAuth/backend error code from the response body, else the error message. */
export const errorDetailFromError = (error: unknown): string | undefined => {
  const data = (error as MaybeHttpError)?.response?.data

  return (
    data?.error ??
    data?.reason ??
    (error instanceof Error ? error.message : undefined)
  )
}
